import { useEffect, useState } from "react";
import { Smartphone, Share, PlusSquare, X } from "lucide-react";

/**
 * AddToHomeScreen — small friendly prompt for mobile visitors.
 *
 * Behavior:
 *   • Only renders on narrow viewports (mobile-first — no desktop overlay).
 *   • Skipped entirely when the app is already running in standalone / PWA mode.
 *   • Skipped when the user has previously dismissed within the last 14 days.
 *   • On Chromium (Android/Chrome/Edge): listens for `beforeinstallprompt`,
 *     lets us fire the native install prompt with a single "Install" button.
 *   • On iOS Safari: `beforeinstallprompt` never fires, so we show a
 *     visual guide — "Tap Share → Add to Home Screen".
 *
 * Never covers content — appears as a compact card fixed to the bottom.
 */
const DISMISS_KEY = "pwa_install_dismissed_at";
const DISMISS_TTL_MS = 14 * 24 * 60 * 60 * 1000; // 14 days

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    // iOS Safari sets a non-standard `navigator.standalone` on home-screen launches.
    window.navigator?.standalone === true
  );
}

function isMobileViewport() {
  if (typeof window === "undefined") return false;
  return window.matchMedia?.("(max-width: 767px)").matches;
}

function isRecentlyDismissed() {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const ts = parseInt(raw, 10);
    if (!Number.isFinite(ts)) return false;
    return Date.now() - ts < DISMISS_TTL_MS;
  } catch {
    return false;
  }
}

function detectPlatform() {
  const ua = (typeof navigator !== "undefined" && navigator.userAgent) || "";
  if (/iPhone|iPad|iPod/i.test(ua)) return "ios";
  if (/Android/i.test(ua)) return "android";
  return "other";
}

export default function AddToHomeScreen() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [platform] = useState(detectPlatform);

  useEffect(() => {
    if (isStandalone()) return;         // already installed → do nothing
    if (!isMobileViewport()) return;    // desktop → never show
    if (isRecentlyDismissed()) return;  // user said "not now"

    // Android/Chrome path — wait for the beforeinstallprompt event.
    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);

    // Hide + clear pref once the user actually installs from any surface.
    const onInstalled = () => {
      setVisible(false);
      setDeferredPrompt(null);
      try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch {}
    };
    window.addEventListener("appinstalled", onInstalled);

    // iOS Safari never fires beforeinstallprompt, so we show the manual card
    // after a brief delay (gives the page time to settle first).
    let iosTimer;
    if (platform === "ios") {
      iosTimer = setTimeout(() => setVisible(true), 1200);
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
      if (iosTimer) clearTimeout(iosTimer);
    };
  }, [platform]);

  if (!visible) return null;

  const dismiss = () => {
    setVisible(false);
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch {}
  };

  const install = async () => {
    if (!deferredPrompt) return;
    try {
      deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice;
      if (choice?.outcome === "accepted") {
        setVisible(false);
      }
    } catch {
      // native prompt already consumed or user cancelled — just hide.
      setVisible(false);
    } finally {
      setDeferredPrompt(null);
    }
  };

  const canInstall = platform === "android" && !!deferredPrompt;
  const isIos = platform === "ios";

  return (
    <div
      className="fixed bottom-4 inset-x-4 z-50 sm:hidden"
      role="dialog"
      aria-labelledby="a2hs-title"
      data-testid="a2hs-prompt"
    >
      <div className="rounded-xl border border-teal-200 bg-white dark:bg-slate-900 dark:border-teal-800 shadow-2xl p-4">
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss install prompt"
          data-testid="a2hs-dismiss"
          className="absolute top-2 right-2 w-8 h-8 flex items-center justify-center text-slate-400 hover:text-slate-700 rounded-md hover:bg-slate-100 dark:hover:bg-slate-800"
        >
          <X className="w-4 h-4" />
        </button>
        <div className="flex items-start gap-3 pr-6">
          <div className="w-10 h-10 rounded-lg bg-teal-50 dark:bg-teal-900/30 flex items-center justify-center shrink-0">
            <img src="/icon-192.png" alt="" className="w-8 h-8 rounded-md" />
          </div>
          <div className="min-w-0 flex-1">
            <div id="a2hs-title" className="text-sm font-bold text-slate-900 dark:text-slate-100">
              Add The Amino Exchange to your Home Screen
            </div>
            <div className="text-[11px] text-slate-500 dark:text-slate-400 leading-snug mt-0.5">
              Opens like an app · works offline for cached pages · same login, tracking, and reminders.
            </div>
          </div>
        </div>

        {canInstall && (
          <div className="mt-3 flex gap-2">
            <button
              type="button"
              onClick={install}
              data-testid="a2hs-install-btn"
              className="flex-1 h-10 rounded-lg bg-[#0D9488] hover:bg-[#0F766E] text-white text-sm font-semibold uppercase tracking-widest"
            >
              Install
            </button>
            <button
              type="button"
              onClick={dismiss}
              data-testid="a2hs-not-now-btn"
              className="h-10 px-3 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 text-xs font-semibold uppercase tracking-widest"
            >
              Not now
            </button>
          </div>
        )}

        {isIos && (
          <div className="mt-3 rounded-lg bg-teal-50 dark:bg-teal-900/20 border border-teal-100 dark:border-teal-800 p-3 space-y-2">
            <div className="flex items-center gap-2 text-[11px] text-slate-700 dark:text-slate-200 font-mono-num">
              <span className="w-5 h-5 rounded-full bg-teal-500 text-white text-[10px] font-bold flex items-center justify-center">1</span>
              <span>Tap the <b>Share</b> icon</span>
              <Share className="w-3.5 h-3.5 text-teal-700 dark:text-teal-300" />
              <span className="text-slate-400">at the bottom of Safari</span>
            </div>
            <div className="flex items-center gap-2 text-[11px] text-slate-700 dark:text-slate-200 font-mono-num">
              <span className="w-5 h-5 rounded-full bg-teal-500 text-white text-[10px] font-bold flex items-center justify-center">2</span>
              <span>Choose <b>Add to Home Screen</b></span>
              <PlusSquare className="w-3.5 h-3.5 text-teal-700 dark:text-teal-300" />
            </div>
          </div>
        )}

        {!canInstall && !isIos && (
          <div className="mt-3 flex items-center gap-1.5 text-[11px] text-slate-500 dark:text-slate-400">
            <Smartphone className="w-3.5 h-3.5" />
            Open your browser menu and choose "Install app" or "Add to Home screen".
          </div>
        )}
      </div>
    </div>
  );
}
